import classNames from 'classnames';
import { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { UserRight } from '@amzn/coral_com-amazon-asxcustomerprofileservice';
import { PlaceEacOrderSubmitResponse } from '@amzn/coral_com-amazon-sssapinterface-model-operations-credits-submit';
import { EntitlementViewBase, ProductViewBase } from '@amzn/coral_com-amazon-sssapinterface-model-structures-credits';
import { useBundle } from '@amzn/react-arb-tools';
import {
  AlertBanner,
  ALERT_BANNER_VARIANT,
  ApiErrorBanner,
  API_ERROR_BANNER_VARIANT,
  ErrorType,
  FlatCard,
  MARKETPLACE_ID,
  SECTION_THEME,
  SignatoryBadge,
  SIGNATORY_BADGE_THEME,
  Tagline,
  TAGLINE_COLORS,
  Datatable,
  CELL_BORDER_POSITION,
  CELL_BORDER_RADIUS,
  CELL_BORDER_VARIANT,
  CELL_HORIZONTAL_ALIGN,
  CELL_VARIANT,
  CELL_FONT_WEIGHT,
  FormattedLink,
  DASHBOARD_ITEM_VARIANT,
  DashboardItem,
  ICON_NAME,
  PrimaryButton,
  useApi,
  useLogger,
  Loader,
  formatTimestampToLongDate,
  useDeviceSize,
} from '@amzn/sss-website-theme';
import tableStyles from '@amzn/sss-website-theme/dist/components/common/datatable/Datatable.module.scss';

import { useSssApiCreditsClient } from 'src/clients';
import { isUkLaunchEnabled, isUkOrderingEnabled } from 'src/clients/weblab/weblab-client';
import { CreditsComputedInfo } from 'src/components/pages/checkout/credits-computed-info/CreditsComputedInfo';
import { CHECKOUT_BUNDLE_NAME, COMMON_BUNDLE_NAME } from 'src/constants/panther/resource-bundles';
import { ROUTES } from 'src/enums/Routes';
import {
  getCurrencySymbol,
  shouldShowOriginalPrice,
  formatProductPrice,
  getImageRoot,
  isOrderingBlockedForStage,
} from 'src/helpers';
import { t } from 'src/helpers/translations/translation-helpers';
import { CREDIT_SEGMENT_TAG_CONFIG, useProductTags, useAsyncOrderPolling } from 'src/hooks';
import { useGetUserPreferences } from 'src/hooks/useGetUserPreferences.hook';
import { useGetUserRights } from 'src/hooks/useGetUserRights.hook';

import styles from './CheckoutStep3.module.scss';

interface CheckoutStep3Props {
  product: ProductViewBase | null | undefined;
  entitlement: EntitlementViewBase | null | undefined;
}

const BASE_CELL_STYLES = classNames(
  tableStyles[CELL_BORDER_POSITION.BOTTOM],
  tableStyles[CELL_BORDER_VARIANT.LIGHT_GREEN]
);

const BASE_HEADER_ROW_STYLES = classNames(
  BASE_CELL_STYLES,
  tableStyles[CELL_VARIANT.HIGHLIGHTED],
  tableStyles[CELL_FONT_WEIGHT.MONO],
  tableStyles[CELL_BORDER_POSITION.TOP]
);

const BASE_ENTRY_ROW_STYLES = classNames(BASE_CELL_STYLES, tableStyles[CELL_VARIANT.SUBTITLE]);

export const CheckoutStep3: FC<CheckoutStep3Props> = ({ product, entitlement }) => {
  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);
  const [commonBundle] = useBundle(COMMON_BUNDLE_NAME);
  const [checkoutBundle] = useBundle(CHECKOUT_BUNDLE_NAME);
  const creditsClient = useSssApiCreditsClient();
  const tags = useProductTags(product);
  const { userPreferredMarketplaceId, isLoading: isLoadingPreferences } = useGetUserPreferences();
  const { userRights, isLoading: isLoadingUserRights } = useGetUserRights();

  const { asin } = useParams<{ asin: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const logger = useLogger();
  const hasSubmittedRef = useRef(false);
  const [hasClickedSubmit, setHasClickedSubmit] = useState(false);

  const { isPolling, orderError, startPolling, clearError, handlePageLoadGuard } = useAsyncOrderPolling({
    entitlementId: entitlement?.entitlementId,
    getEacEntitlement: creditsClient.getEacEntitlement,
    onSuccess: () => navigate(0),
    onError: (err) => logger.logError('CheckoutStep3', 'Error polling entitlement status.', err, { asin }),
  });

  const customErrorHandler = (err: Error) => {
    // istanbul ignore next
    logger.logError('CheckoutStep3', 'Error placing an order for this Entitlement.', err, { asin });
  };

  const { isLoading: isSubmitting, response, callApi } = useApi<PlaceEacOrderSubmitResponse>(customErrorHandler);

  // Reset hasClickedSubmit when the submit flow reaches any terminal state:
  // - Retryable error (polling detected FAILED) → user can retry
  // - Non-retryable error (timeout) → button disabled but label should be normal
  // - Submit failed (API threw, no response) → user can retry
  // hasClickedSubmit only needs to be true during the brief gap between
  // useApi resolving and polling starting (prevents spinner flash).
  const wasSubmittingRef = useRef(false);
  useEffect(() => {
    if (isSubmitting) {
      wasSubmittingRef.current = true;
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (!hasClickedSubmit) return;

    const submitFailed = wasSubmittingRef.current && !isSubmitting && !response && !isPolling;
    const pollingEnded = !isPolling && orderError !== null;

    if (submitFailed || pollingEnded) {
      setHasClickedSubmit(false);
      wasSubmittingRef.current = false;
    }
  }, [hasClickedSubmit, isSubmitting, response, isPolling, orderError]);

  // Handle response from placeEacOrder
  useEffect(() => {
    if (!response) return;

    if (response.orderId) {
      navigate(0);
    } else if (response.workflowExecutionId) {
      startPolling(response.workflowExecutionId);
    }
  }, [response, navigate, startPolling]);

  // Guard: if orderAttempt exists on page load (e.g. customer refreshes mid-workflow)
  useEffect(() => {
    if (hasSubmittedRef.current) return;
    handlePageLoadGuard(entitlement);
  }, [entitlement, handlePageLoadGuard]);

  const onSubmit = () => {
    if (!asin || !entitlement) return;

    setHasClickedSubmit(true);
    hasSubmittedRef.current = true;
    clearError();
    const placeOrder = async () => creditsClient.placeEacOrder(entitlement.entitlementId);
    callApi(placeOrder);
  };

  // Full-page spinner only when data is loading — NOT after user clicks submit
  if (!product || !entitlement || (isSubmitting && !hasClickedSubmit && !isPolling)) {
    return (
      <div className={styles.loaderWrapper}>
        <Loader label={t(commonBundle, 'loading_aria_label')} />
      </div>
    );
  }

  const currencySymbol = getCurrencySymbol(entitlement.entitlementCurrencyCode ?? product.productOfferCurrency);
  const showOriginalPrice = shouldShowOriginalPrice(product);

  const orderNeedByDateString = formatTimestampToLongDate(entitlement.entitlementNeedByDate);

  const orderQuantityString = t(checkoutBundle, 'checkout_step_3_highlightedBanner_1_value', {
    quantity: entitlement.entitlementQuantity.toLocaleString(),
  });

  // Block ordering in Gamma so testing there cannot create real orders against
  // the shared production backend. Derived from the build-time stage token, not
  // the browser URL. See src/helpers/stage/stage-helpers.ts.
  const isOrderingBlockedByStage = isOrderingBlockedForStage();

  const disableCheckout =
    isLoadingPreferences ||
    isOrderingBlockedByStage ||
    (userPreferredMarketplaceId === MARKETPLACE_ID.EU && !isUkOrderingEnabled);

  // VAT verification gating for UK marketplace (ASX-1919)
  // Only evaluate once user rights have loaded successfully. An empty array after loading
  // indicates a fetch failure — in that case we keep the button disabled but don't show
  // the misleading VAT rejection banner.
  const isUkMarketplace = userPreferredMarketplaceId === MARKETPLACE_ID.EU;
  const hasUserRightsLoaded = !isLoadingUserRights && userRights.length > 0;
  const isVatPending = userRights.includes(UserRight.UK_VAT_PENDING);
  const isVatVerified = userRights.includes(UserRight.UK_VAT_VERIFIED);
  const showVatErrorBanner = hasUserRightsLoaded && isUkMarketplace && !isVatVerified && !isVatPending;
  const showVatInfoBanner = hasUserRightsLoaded && isUkMarketplace && isVatPending;

  // Disable button during submit, polling, after non-retryable error, or when VAT is blocked/pending
  const isButtonDisabled =
    isSubmitting ||
    isPolling ||
    hasClickedSubmit ||
    (orderError !== null && !orderError.isRetryable) ||
    showVatErrorBanner ||
    showVatInfoBanner ||
    (isUkMarketplace && (isLoadingUserRights || userRights.length === 0));

  const getButtonLabel = (): string => {
    if (isPolling || hasClickedSubmit) {
      return t(checkoutBundle, 'checkout_step_3_processing_order');
    }
    return t(checkoutBundle, 'checkout_proceed_to_payment');
  };

  const projectMetadataTable = {
    rows: [
      {
        cells: [
          {
            content: t(checkoutBundle, 'checkout_carbon_credit_projects_table_row_1_column_1'),
            className: classNames(
              BASE_HEADER_ROW_STYLES,
              tableStyles[CELL_BORDER_RADIUS.TOP_LEFT],
              tableStyles[CELL_BORDER_POSITION.LEFT]
            ),
          },
          {
            content: t(checkoutBundle, 'checkout_carbon_credit_projects_table_row_1_column_2'),
            className: classNames(
              BASE_HEADER_ROW_STYLES,
              tableStyles[CELL_HORIZONTAL_ALIGN.START],
              tableStyles[CELL_BORDER_POSITION.HORIZONTAL]
            ),
          },
          {
            content: t(checkoutBundle, 'checkout_carbon_credit_projects_table_row_1_column_4'),
            className: classNames(
              BASE_HEADER_ROW_STYLES,
              tableStyles[CELL_HORIZONTAL_ALIGN.START],
              tableStyles[CELL_BORDER_POSITION.RIGHT]
            ),
          },
          {
            content: t(checkoutBundle, 'checkout_carbon_credit_projects_table_row_1_column_5'),
            className: classNames(
              BASE_HEADER_ROW_STYLES,
              tableStyles[CELL_HORIZONTAL_ALIGN.START],
              tableStyles[CELL_BORDER_RADIUS.TOP_RIGHT],
              tableStyles[CELL_BORDER_POSITION.RIGHT]
            ),
          },
        ],
      },
      {
        cells: [
          {
            content: product.productCountryOfOrigin ?? '',
            className: classNames(
              BASE_ENTRY_ROW_STYLES,
              tableStyles[CELL_BORDER_POSITION.LEFT],
              tableStyles[CELL_BORDER_RADIUS.BOTTOM_LEFT]
            ),
          },
          {
            content: product.productVintage ?? '',
            className: classNames(BASE_ENTRY_ROW_STYLES, tableStyles[CELL_BORDER_POSITION.HORIZONTAL]),
          },
          {
            content: product.productMethodology ?? '',
            className: classNames(BASE_ENTRY_ROW_STYLES, tableStyles[CELL_BORDER_POSITION.RIGHT]),
          },
          {
            content: orderQuantityString,
            className: classNames(
              BASE_ENTRY_ROW_STYLES,
              tableStyles[CELL_BORDER_POSITION.RIGHT],
              tableStyles[CELL_BORDER_RADIUS.BOTTOM_RIGHT]
            ),
          },
        ],
      },
    ],
  };

  return (
    <section className={styles.checkoutStep3}>
      <FlatCard
        className={classNames(styles.card, styles.firstCard)}
        image={
          product.productDetailAtfHeroImage
            ? { src: `${imageRoot}/${product.productDetailAtfHeroImage}`, alt: product.productTitle }
            : undefined
        }
        variant={SECTION_THEME.Teal}
        title={t(checkoutBundle, 'checkout_step_3_first_card_title')}
        rows={[
          {
            label: t(checkoutBundle, 'checkout_first_card_name_title'),
            value: product.productTitle,
          },
          {
            label: t(checkoutBundle, 'checkout_first_card_segment_title'),
            value: t(commonBundle, CREDIT_SEGMENT_TAG_CONFIG[product.productCreditSegment].stringId),
          },
        ]}
      >
        <div className={styles.content}>
          <div className={styles.carbonCreditType}>
            <p className={styles.title}>{t(checkoutBundle, 'checkout_first_card_type_title')}</p>
            <ul className={styles.taglineList}>
              {tags.map((tag, i) => (
                <li key={`tag-${i}-${tag.copy}`}>
                  <Tagline className={styles.tagline} icon={tag.icon} color={TAGLINE_COLORS.TRANSPARENT}>
                    {tag.copy}
                  </Tagline>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.contractPrice}>
            <p className={styles.title}>{t(checkoutBundle, 'checkout_first_card_price_title')}</p>
            <div className={styles.priceRow}>
              {showOriginalPrice && (
                <p className={classNames(styles.body, styles.originalPrice)} data-testid="checkout-original-price">
                  {currencySymbol}
                  {formatProductPrice(product.productBaseOfferPrice)}
                </p>
              )}
              <p className={styles.body}>
                {t(
                  checkoutBundle,
                  isUkLaunchEnabled ? 'checkout_first_card_per_credit' : 'checkout_first_card_price_label',
                  {
                    currencySymbol,
                    price: formatProductPrice(product.productMatchedOfferPrice),
                  }
                )}
              </p>
              {showOriginalPrice && (
                <SignatoryBadge
                  className={styles.signatoryBadge}
                  theme={SIGNATORY_BADGE_THEME.Light}
                  data-testid="checkout-signatory-badge"
                >
                  {t(checkoutBundle, 'checkout_step_1_signatory_price_tag')}
                </SignatoryBadge>
              )}
            </div>
          </div>

          <div className={styles.carbonCreditsProjects}>
            <p className={styles.carbonCreditsProjectsTitle}>{t(checkoutBundle, 'checkout_carbon_credit_projects')}</p>
            <Datatable minWidth={0} rows={projectMetadataTable.rows} />
            <FormattedLink
              openNewTab
              text={t(checkoutBundle, 'checkout_carbon_report_link')}
              link="https://acr2.apx.com/mymodule/reg/TabDocuments.asp?aProj=icrt&type=PRO&r=617&ad=MP&act=update&tablename=doc&id1=1129&iCr="
              className={styles.carbonCreditsProjectsLink}
            />
          </div>
        </div>
      </FlatCard>
      <FlatCard
        tagline={`${t(checkoutBundle, 'checkout_request_id')}${entitlement.entitlementId}`}
        className={classNames(styles.card, styles.secondCard)}
        title={t(checkoutBundle, 'checkout_step_3_second_card_title')}
      >
        <div className={styles.highlightWrapper}>
          <CreditsComputedInfo
            product={product}
            entitlement={entitlement}
            quantity={entitlement.entitlementQuantity}
            deliveryDate={orderNeedByDateString}
            marketplaceId={entitlement.obfuscatedMarketplaceId}
            showBundleNote={false}
            variant="confirmed"
          />
          <DashboardItem
            variant={DASHBOARD_ITEM_VARIANT.Disclaimer}
            icon={ICON_NAME.INFO_FILLED}
            label={t(
              checkoutBundle,
              !disableCheckout ? 'checkout_step_3_disclaimer_copy' : 'checkout_step_3_disclaimer_notify'
            )}
          />
        </div>

        {orderError && (
          <ApiErrorBanner
            errors={[
              {
                id: 'order-error',
                error: new Error(
                  orderError.isRetryable
                    ? t(checkoutBundle, 'checkout_step_3_error_retryable')
                    : t(checkoutBundle, 'checkout_step_3_error_non_retryable')
                ),
                type: ErrorType.SERVER,
                timestamp: Date.now(),
              },
            ]}
            errorMessages={{
              generic: orderError.isRetryable
                ? t(checkoutBundle, 'checkout_step_3_error_retryable')
                : t(checkoutBundle, 'checkout_step_3_error_non_retryable'),
            }}
            iconName={ICON_NAME.ERROR}
            variant={API_ERROR_BANNER_VARIANT.ERROR}
            testId="order-error-banner"
          />
        )}

        {showVatErrorBanner && (
          <AlertBanner
            icon={ICON_NAME.ERROR_OUTLINED}
            className={styles.vatBanner}
            boldText={t(checkoutBundle, 'checkout_step_3_vat_rejected_banner_bold')}
            message={t(checkoutBundle, 'checkout_step_3_vat_rejected_banner')}
            variant={ALERT_BANNER_VARIANT.Error}
            ctaLabel={t(checkoutBundle, 'checkout_step_3_vat_review_cta')}
            ctaHref={`${ROUTES.ACCOUNT_EDIT_BILLING_INFO}?redirectTo=${encodeURIComponent(location.pathname)}`}
            testId="vat-error-banner"
          />
        )}

        {showVatInfoBanner && (
          <AlertBanner
            icon={ICON_NAME.INFO_OUTLINED}
            className={styles.vatBanner}
            boldText={t(checkoutBundle, 'checkout_step_3_vat_pending_banner_bold')}
            message={t(checkoutBundle, 'checkout_step_3_vat_pending_banner')}
            variant={ALERT_BANNER_VARIANT.Info}
            testId="vat-info-banner"
          />
        )}

        <PrimaryButton
          label={getButtonLabel()}
          onClick={onSubmit}
          icon={isPolling || hasClickedSubmit ? undefined : ICON_NAME.CHEVRON_RIGHT}
          className={styles.secondCardCta}
          data-testid="place-order-cta"
          disabled={isButtonDisabled || disableCheckout}
          isRebrandingActive
        />
      </FlatCard>
    </section>
  );
};
